/*
 * Created by Dmytro Sav. on 5/20/2020
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */

import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormGroup } from '@angular/forms';
import { Course } from '../course';
import { getRestApiProvider, Paginated, RestApiService } from '../../../shared/api/rest-api.service';
import { CoursesService } from '../courses.service';
import { CurriculumManagementModel } from '../../curriculum-managment/curriculum-management.model';
import { Options } from '../../../shared/components/dropdown/dropdown.component';
import {of, Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {takeUntil} from 'rxjs/operators';

interface DialogData {
  courseId: any;
  form: FormGroup;
  title: string;
  curriculumId?: number;
  courseIdValue?: number;
}

@Component({
  selector: 'curr-create-course-popup',
  templateUrl: './create-course-popup.component.html',
  styleUrls: ['./create-course-popup.component.scss'],
  providers: [
    getRestApiProvider<Course>('schoolYearCourses/unassignedCourses', 'unassignedCourses'),
    getRestApiProvider<CurriculumManagementModel>('curriculums/management', 'curriculumsManagement')
  ]
})
export class CreateCoursePopupComponent implements OnInit, OnDestroy {
  courseId = '';
  filteredCourses: Course[] = [];
  form: FormGroup;
  title: string;
  isEdited = false;
  activeCourseId: string;
  isCreatedCourse = true;

  curriculumId: string;
  curriculums: Options[];

  unassignedCourses: Course[];
  selectedCourse: Course;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CreateCoursePopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    @Inject('unassignedCourses') private unassignedCoursesService: RestApiService<Course>,
    @Inject('curriculumsManagement') private curriculumManagementService: RestApiService<{
      items: CurriculumManagementModel[],
      currentPage: number,
      hasNextPage: boolean,
      total: number,
    }>,
    private coursesService: CoursesService,
    private toastr: ToastrService,
  ) {
  }

  ngOnInit() {
    this.form = this.data.form;
    if (this.data.title) {
      this.title = this.data.title;
      this.isCreatedCourse = true;
      this.isEdited = true;

      this.curriculumManagementService.getAllPaginated(
        of(new Paginated(100, 0)),
        { is_published: true }
      ).subscribe(res => {

        this.curriculums = res.items
          .sort((a, b) => a.curriculum_name.localeCompare(b.curriculum_name))
          .map(curriculum => ({
            value: curriculum.curriculum_id,
            label: curriculum.curriculum_name
          }));

        const selectedCurriculum = res.items.find(c => +c.curriculum_id === this.data.curriculumId);
        if (selectedCurriculum) {
          this.curriculumId = selectedCurriculum.curriculum_id;
        } else if (res.items.length > 0) {
          this.curriculumId = '';
        }
      });
    } else {
      this.title = 'Create course';
      this.unassignedCoursesService.getAll().subscribe(res => {
        this.unassignedCourses = res;
        this.filteredCourses = res;
      });
      this.curriculumManagementService.getAllPaginated(
        of(new Paginated(100, 0)),
        { is_published: true }
      ).subscribe(res => {
        this.curriculums = res.items
          .sort((a, b) => a.curriculum_name.localeCompare(b.curriculum_name))
          .map(curriculum => ({
            value: curriculum.curriculum_id,
            label: curriculum.curriculum_name
          }));
        this.curriculumId = res[0].curriculum_id;
      });
    }
    if (this.data.courseId) {
    this.courseId = this.data.courseId;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCourseById() {
    if (!this.isCreatedCourse) {
      if (this.courseId && this.courseId !== '') {
        this.filteredCourses = this.unassignedCourses.filter(course => {
          return course.id.toString().includes(this.courseId);
        });
      } else {
        this.filteredCourses = this.unassignedCourses;
      }
    }
  }

  addCourse() {
    // this.data.form = this.form;
    if (!this.isCreatedCourse) {
      this.assignCourse();
    } else {
      this.createCourse();
    }
  }

  saveCourse() {
    const courseIdKey = Number(this.data.courseIdValue);
    if (Number.isNaN(courseIdKey)) {
      this.toastr.error('Invalid course id');
      return;
    }
    if (!this.courseId || this.courseId === '') {
      this.toastr.error('Please enter Course ID');
      return;
    }
    if (!this.curriculumId || this.curriculumId === '') {
      this.toastr.error('Please select Scheme of Work');
      return;
    }
    this.coursesService.editCourse(courseIdKey, {courseId: this.courseId})
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      resp => {
        this.dialogRef.close(this.data);
        this.toastr.success('Course was updated successfully');
      },
      error => {
        this.toastr.error(error.error.message);
      });
  }

  assignCourse() {
    this.coursesService.assignCourse(this.selectedCourse.id, this.curriculumId).pipe(takeUntil(this.destroy$)).subscribe(
      resp => {
        this.dialogRef.close(this.data);
        this.toastr.success('Course was assigned successfully');
      },
      error => {
        this.toastr.error(error.error.message);
      });
  }

  createCourse() {
    if (!this.courseId || this.courseId === '') {
      this.toastr.error('Please enter Course ID');
      return;
    }
    if (!this.curriculumId || this.curriculumId === '') {
      this.toastr.error('Please select Scheme of Work');
      return;
    }
    this.coursesService.createCourse(this.courseId, this.curriculumId).pipe(takeUntil(this.destroy$)).subscribe(
      resp => {
        this.dialogRef.close(this.data);
        this.toastr.success('Course was created successfully');
      },
      error => {
        this.toastr.error(error.error.message);
      });
  }
}
